import { useEffect, useRef } from "react"
import { useAuth, type User } from "@/hooks/useAuth"
import { authenticationApi, forceLogout } from "@/lib/api"
import { LoginUserDTORoleEnum } from "@/gen/api"

const AUTH_REFRESH_INTERVAL_MS = 60_000

export const useAuthValidation = () => {
  const { user, isAuthenticated, setUser } = useAuth()
  const userRef = useRef(user)
  const setUserRef = useRef(setUser)
  const activePollingUserIdRef = useRef<number>()

  useEffect(() => {
    userRef.current = user
    setUserRef.current = setUser
  }, [user, setUser])

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return
    if (activePollingUserIdRef.current === user.id) return
    activePollingUserIdRef.current = user.id

    let cancelled = false
    let timeoutId: number | undefined

    const validateAuth = async () => {
      const currentUser = userRef.current
      if (!currentUser) return

      try {
        const response = await authenticationApi.getCurrentUser()
        const freshUserData = (response.data as { data?: Record<string, unknown> }).data as
          | Record<string, unknown>
          | undefined
        if (!freshUserData || cancelled) return
        const councils = (freshUserData["councils"] as Record<string, unknown>[] | undefined) ?? []
        const council = councils.length > 0 ? councils[0] : null
        const userData: User = {}
        const id = freshUserData["id"] as number | undefined
        if (id !== undefined) userData.id = id
        const email = freshUserData["email"] as string | undefined
        if (email !== undefined) userData.email = email
        const role = freshUserData["role"] as LoginUserDTORoleEnum | undefined
        if (role !== undefined) userData.role = role
        userData.councils = (councils as User["councils"]) ?? []
        userData.permissions =
          (freshUserData["permissions"] as User["permissions"] | undefined) ??
          currentUser.permissions ??
          []
        const councilId = council?.["id"] as number | undefined
        if (councilId !== undefined) userData.councilId = councilId
        const name = freshUserData["name"] as string | undefined
        if (name !== undefined) userData.name = name
        setUserRef.current(userData)
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } }
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          forceLogout()
        }
      }
    }

    validateAuth()
    const scheduleRefresh = () => {
      timeoutId = window.setTimeout(async () => {
        await validateAuth()
        if (!cancelled) scheduleRefresh()
      }, AUTH_REFRESH_INTERVAL_MS)
    }
    scheduleRefresh()

    return () => {
      cancelled = true
      if (activePollingUserIdRef.current === user.id) activePollingUserIdRef.current = undefined
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [user?.id, isAuthenticated])
}
